import React from 'react';

import { MedicationDetails } from '../../../types';
import { ClinicalMedication } from '../types';
import { formatDate, formatMedicationCode } from '../utils/formatters';
import { findHeroMedication } from '../utils/heroMedications';
import * as Style from '../style.module.scss';
import EhrCard from './EhrCard';
import StatusPill from './StatusPill';

const formatRegimen = (medication: ClinicalMedication) =>
    [
        medication.frequency,
        medication.route,
        medication.date_stopped_taking
            ? `Stopped ${formatDate(medication.date_stopped_taking)}`
            : null
    ]
        .filter(Boolean)
        .join(' · ');

/**
 * Mirrors Health Assessment's Inside/Outside Hero lists. A medication missing
 * from the member's Hero configuration gets no label — "Outside Hero" means
 * configured but not in the device, not "unknown to Hero".
 */
const HeroPlacement: React.FC<{ name: string | null; heroMedications: MedicationDetails[] }> = ({
    name,
    heroMedications
}) => {
    const match = findHeroMedication(name, heroMedications);

    if (!match) {
        return null;
    }

    return match.stored_in_hero ? (
        <StatusPill
            label="Inside Hero"
            tone="primary"
            tooltipLabel={
                match.slot ? `Hero slot ${match.slot}: ${match.name}` : `In Hero as ${match.name}`
            }
        />
    ) : (
        <StatusPill
            label="Outside Hero"
            tone="secondary"
            tooltipLabel={`In Hero configuration as ${match.name}`}
        />
    );
};

type Props = {
    medications: ClinicalMedication[];
    /** Inside-Hero first, so a name matching both lists resolves to Inside Hero. */
    heroMedications: MedicationDetails[];
    lastSynced?: string;
};

const Medications: React.FC<Props> = ({ medications, heroMedications, lastSynced }) => (
    <EhrCard
        title="Medications"
        meta={lastSynced}
        isEmpty={!medications.length}
        emptyMessage="No medications on file."
    >
        <div className={`${Style.list} ${Style.withCode}`}>
            {medications.map((medication) => {
                const regimen = formatRegimen(medication);

                return (
                    <div key={medication.id} className={Style.listRow}>
                        <div>
                            <span>{medication.name || 'Unnamed'}</span>
                            {medication.prn ? <span className={Style.chipInline}>PRN</span> : null}
                            {medication.removed_at ? (
                                <span className={Style.removed}>
                                    {`Removed ${formatDate(medication.removed_at)}`}
                                </span>
                            ) : null}
                            {regimen ? <span className={Style.subText}>{regimen}</span> : null}
                            {medication.notes ? (
                                <span className={Style.subText}>{medication.notes}</span>
                            ) : null}
                        </div>
                        <span className={Style.code}>{formatMedicationCode(medication)}</span>
                        <HeroPlacement name={medication.name} heroMedications={heroMedications} />
                    </div>
                );
            })}
        </div>
    </EhrCard>
);

export default Medications;
